import { useState } from "react";
import { ClipboardList } from "lucide-react";
import { useI18n } from "../../../i18n";
import { useAuth } from "../../../context/AuthContext";
import { useOrders } from "../hooks";
import { OrderRow } from "../orders/OrderRow";
import { ApprovalActions } from "../orders/ApprovalActions";
import { EmptyState, ErrorNote, SkeletonBlock } from "../../../components/common/EmptyState";

const PAGE = 25;

const Awaiting = ({ orders, approver }) => {
  const { t } = useI18n();
  if (!orders.length) return null;
  return (
    <section data-testid="awaiting-approval">
      <h2 className="text-base font-semibold text-navy md:text-lg">{t("orders.awaiting")} ({orders.length})</h2>
      <ul className="mt-2 divide-y divide-amber-100 overflow-hidden rounded-2xl border border-amber-200 bg-amber-50/50">
        {orders.map((o) => (
          <li key={o.local_id}>
            <OrderRow order={o} />
            {approver && <div className="px-4 pb-3"><ApprovalActions order={o} /></div>}
          </li>
        ))}
      </ul>
    </section>
  );
};

export default function Orders() {
  const { t } = useI18n();
  const { user } = useAuth();
  const orders = useOrders();
  const [shown, setShown] = useState(PAGE);
  const approver = user.permissions.includes("orders:approve");
  const all = orders.data || [];
  const pending = all.filter((o) => o.status === "pending_approval");
  const rest = all.filter((o) => o.status !== "pending_approval");

  return (
    <div className="space-y-5" data-testid="orders-page">
      <h1 className="text-2xl font-bold text-navy sm:text-3xl">{t("orders.title")}</h1>
      {orders.isError && <ErrorNote text={t("common.error")} />}
      {orders.isLoading ? <div className="space-y-2">{[0, 1, 2, 3, 4].map((i) => <SkeletonBlock key={i} className="h-16" />)}</div> : (
        <>
          <Awaiting orders={pending} approver={approver} />
          {rest.length === 0 && !pending.length ? <EmptyState icon={ClipboardList} title={t("orders.empty")} sub={t("orders.emptySub")} testId="orders-empty" /> : (
            <ul className="divide-y divide-slate-100 overflow-hidden rounded-2xl bg-white shadow-sm" data-testid="orders-list">
              {rest.slice(0, shown).map((o) => <li key={o.local_id}><OrderRow order={o} /></li>)}
            </ul>
          )}
          {rest.length > shown && (
            <button type="button" onClick={() => setShown((s) => s + PAGE)} data-testid="orders-show-more" className="mx-auto flex h-11 items-center rounded-full bg-white px-6 text-sm font-semibold text-navy shadow-sm">{t("orders.showMore")}</button>
          )}
        </>
      )}
    </div>
  );
}
